import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Divider, FormControl, InputLabel,
  LinearProgress, MenuItem, Paper, Select, Stack, Step, StepLabel, Stepper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  alpha, useTheme,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { ModelsIcon } from '../global/CustomIcons';

import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';
import PageBreadcrumbs from '../common/PageBreadcrumbs';

import {
  fetchRequest as uploadRequest,
  clearState as clearUpload,
} from '../../redux/actions/foundation/byomUpload';
import {
  fetchRequest as validateRequest,
  clearState as clearValidate,
} from '../../redux/actions/foundation/byomValidate';
import {
  fetchRequest as registerRequest,
  clearState as clearRegister,
} from '../../redux/actions/foundation/byomRegister';
import { fetchRequest as fetchSegments } from '../../redux/actions/segments/list';

const ACCEPTED = '.onnx,.pt,.pth,.h5,.keras,.pkl,.joblib,.json,.ubj,.bin';

const FRAMEWORK_OPTIONS = [
  { value: '',           label: 'Auto-detect' },
  { value: 'onnx',       label: 'ONNX' },
  { value: 'pytorch',    label: 'PyTorch (TorchScript)' },
  { value: 'tensorflow', label: 'TensorFlow / Keras' },
  { value: 'sklearn',    label: 'scikit-learn' },
  { value: 'xgboost',    label: 'XGBoost' },
];

const formatBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const shapeLabel = (shape) => `[${(shape || []).map((d) => (d === -1 ? '∗' : d)).join(', ')}]`;

// Renders the input/output signature blocks returned by /byom/validate.
const SignatureTable = ({ title, fields }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{
      fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>
      {title}
    </Typography>
    {(fields || []).length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Not detected
      </Typography>
    ) : (
      <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ mt: 0.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Shape</TableCell>
            <TableCell>Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f, i) => (
            <TableRow key={f.name || i}>
              <TableCell><code>{f.name}</code></TableCell>
              <TableCell><code>{shapeLabel(f.shape)}</code></TableCell>
              <TableCell>{f.dtype}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </Box>
    )}
  </Box>
);

const UploadModelPage = ({
  upload, validation, registration, segments,
  uploadRequest, validateRequest, registerRequest, fetchSegments,
  clearUpload, clearValidate, clearRegister,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const workspaceId = localStorage.getItem('WORKSPACE_ID');

  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Validation options
  const [frameworkHint, setFrameworkHint] = useState('');
  const [shapeHint, setShapeHint] = useState('');

  // Registration form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [license, setLicense] = useState('proprietary');
  const [segmentId, setSegmentId] = useState('');

  // Reset all three slices when entering the wizard.
  useEffect(() => {
    clearUpload(); clearValidate(); clearRegister();
    if (workspaceId) fetchSegments({ workspace_id: workspaceId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runValidate = useCallback((modelId) => {
    const payload = { model_id: modelId };
    if (frameworkHint) payload.framework = frameworkHint;
    if (shapeHint.trim()) {
      const parsed = shapeHint.split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      if (parsed.length) payload.input_shape_hint = parsed;
    }
    validateRequest(payload);
  }, [frameworkHint, shapeHint, validateRequest]);

  // Auto-advance: upload done -> validate; first validation result -> step 2.
  useEffect(() => {
    if (upload.response?.model_id && activeStep === 0) {
      setActiveStep(1);
      runValidate(upload.response.model_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload.response]);

  useEffect(() => {
    if (registration.response?.blueprint_id) setActiveStep(3);
  }, [registration.response]);

  const handleFileChosen = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    if (!name) setName(chosen.name.replace(/\.[^.]+$/, ''));
    uploadRequest({ file: chosen });
  };

  const handleRegister = () => {
    const v = validation.response;
    registerRequest({
      model_id: upload.response.model_id,
      model_uri: upload.response.model_uri,
      framework: v?.validation?.framework || v?.detection?.framework,
      signature: v?.signature || {},
      validation: v?.validation || {},
      name: name.trim(),
      description: description.trim(),
      visibility,
      license,
      ...(segmentId ? { segment_id: segmentId } : {}),
    });
  };

  const v = validation.response;
  const validationOk = Boolean(v?.validation?.ok);
  const detectedFramework = v?.validation?.framework || v?.detection?.framework;

  // ----- Step 1: upload ------------------------------------------------
  const uploadStep = (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          handleFileChosen(e.dataTransfer.files?.[0]);
        }}
        sx={{
          border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 2,
          p: 5,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
          transition: 'all 120ms ease',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          hidden
          onChange={(e) => handleFileChosen(e.target.files?.[0])}
        />
        <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ mt: 1 }}>
          Drop your model file here, or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ONNX (.onnx), TorchScript (.pt/.pth), Keras (.h5/.keras),
          scikit-learn (.pkl/.joblib), XGBoost (.json/.ubj/.bin)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Upload size limits depend on your plan. The file stays private to
          your workspace and can be downloaded again at any time.
        </Typography>
      </Box>

      {upload.isLoading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Uploading {file?.name} ({formatBytes(file?.size)})… {upload.progress}%
          </Typography>
          <LinearProgress variant="determinate" value={upload.progress} />
        </Box>
      )}

      {upload.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {upload.error.message}
          {upload.error.code === 'plan_limit' && (
            <> — upgrade your plan to raise the BYOM limits.</>
          )}
        </Alert>
      )}
    </Paper>
  );

  // ----- Step 2: validate ----------------------------------------------
  const validateStep = (
    <Paper variant="outlined" sx={{ p: 3 }}>
      {validation.isLoading && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={22} />
          <Typography>
            Validating {file?.name}: detecting the framework, inferring the
            signature, and running a live forward pass…
          </Typography>
        </Stack>
      )}

      {validation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>{validation.error.message}</Alert>
      )}

      {v && !validation.isLoading && (
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {validationOk ? (
              <Chip icon={<CheckCircleOutlineIcon />} color="success" label="Validation passed" />
            ) : (
              <Chip icon={<ErrorOutlineIcon />} color="error" label="Validation failed" />
            )}
            <Chip variant="outlined" label={`Framework: ${detectedFramework || 'unknown'}`} />
            {v.detection?.confidence !== undefined && (
              <Chip variant="outlined" label={`Confidence: ${Math.round((v.detection.confidence || 0) * 100)}%`} />
            )}
            <Chip variant="outlined" label={`Size: ${formatBytes(v.validation?.file_size_bytes)}`} />
            {v.validation?.forward_latency_ms != null && (
              <Chip variant="outlined" label={`Forward pass: ${v.validation.forward_latency_ms.toFixed(1)} ms`} />
            )}
            {v.validation?.output_shape && (
              <Chip variant="outlined" label={`Output: ${shapeLabel(v.validation.output_shape)}`} />
            )}
          </Stack>

          {(v.validation?.static_errors || []).map((msg, i) => (
            <Alert key={`e${i}`} severity="error" icon={<ErrorOutlineIcon />}>{msg}</Alert>
          ))}
          {(v.validation?.static_warnings || []).map((msg, i) => (
            <Alert key={`w${i}`} severity="warning" icon={<WarningAmberOutlinedIcon />}>{msg}</Alert>
          ))}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <SignatureTable title="Inputs" fields={v.signature?.input_signature} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SignatureTable title="Outputs" fields={v.signature?.output_signature} />
            </Box>
          </Stack>

          <Divider />

          <Typography variant="body2" color="text.secondary">
            Wrong detection or missing signature? Override the framework or
            give an input shape hint (e.g. <code>1, 96</code>) and re-run.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Framework</InputLabel>
              <Select
                value={frameworkHint}
                label="Framework"
                onChange={(e) => setFrameworkHint(e.target.value)}
              >
                {FRAMEWORK_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Input shape hint"
              placeholder="1, 96"
              value={shapeHint}
              onChange={(e) => setShapeHint(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <CMButtonOutlined
              startIcon={<ReplayIcon />}
              onClick={() => runValidate(upload.response.model_id)}
            >
              Re-run validation
            </CMButtonOutlined>
          </Stack>
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <CMButtonOutlined onClick={() => { setActiveStep(0); clearValidate(); }}>
          Back
        </CMButtonOutlined>
        <CMButton disabled={!validationOk} onClick={() => setActiveStep(2)}>
          Continue to registration
        </CMButton>
      </Stack>
    </Paper>
  );

  // ----- Step 3: register ----------------------------------------------
  const incompatible = registration.error?.code === 'byom_incompatible_segment';
  const registerStep = (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <TextField
          label="Model name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Visibility</InputLabel>
            <Select value={visibility} label="Visibility" onChange={(e) => setVisibility(e.target.value)}>
              <MenuItem value="private">Private — only your workspace</MenuItem>
              <MenuItem value="public">Public — listed in the catalog</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="License"
            value={license}
            onChange={(e) => setLicense(e.target.value)}
            fullWidth
          />
        </Stack>

        <FormControl fullWidth>
          <InputLabel>Bind to a segment (optional)</InputLabel>
          <Select
            value={segmentId}
            label="Bind to a segment (optional)"
            onChange={(e) => setSegmentId(e.target.value)}
          >
            <MenuItem value="">
              <em>No segment — register the model only</em>
            </MenuItem>
            {(segments || []).map((s) => (
              <MenuItem key={s.uid} value={s.uid}>
                {s.name} ({(s.features || []).length + (s.engineered_features || []).length} features, {(s.labels || []).length} labels)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
          Binding creates a ready-to-promote pipeline. The model's signature is
          checked against the segment's features and labels before anything is
          saved, so mismatches are caught here — not at inference time.
        </Typography>

        {registration.error && (
          <Alert severity="error">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {registration.error.message}
            </Typography>
            {incompatible && (registration.error.details?.issues || []).map((issue, i) => (
              <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>• {issue}</Typography>
            ))}
            {incompatible && registration.error.details?.expected && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Segment layout: {registration.error.details.expected.n_features} features
                ({(registration.error.details.expected.features || []).join(', ')}) → {registration.error.details.expected.n_labels} labels
                ({(registration.error.details.expected.labels || []).join(', ')})
              </Typography>
            )}
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between">
          <CMButtonOutlined onClick={() => setActiveStep(1)}>Back</CMButtonOutlined>
          <CMButton
            disabled={!name.trim() || registration.isLoading}
            onClick={handleRegister}
            startIcon={registration.isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {registration.isLoading ? 'Registering…' : 'Register model'}
          </CMButton>
        </Stack>
      </Stack>
    </Paper>
  );

  // ----- Step 4: done ----------------------------------------------------
  const doneStep = (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
      <Typography variant="h5" sx={{ mt: 1 }}>Model registered</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {registration.response?.model_id
          ? 'Your model is registered and bound to the segment — a ready-to-promote pipeline was created.'
          : 'Your model is registered. Create a pipeline to bind it to a segment and deploy it.'}
      </Typography>
      <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
        <CMButtonOutlined onClick={() => navigate(`/model/${registration.response?.blueprint_id}`)}>
          View model
        </CMButtonOutlined>
        {registration.response?.model_id ? (
          <CMButton onClick={() => navigate('/pipelines')}>Go to pipelines</CMButton>
        ) : (
          <CMButton onClick={() => navigate('/pipelines/create')}>Create a pipeline</CMButton>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto' }}>
      <PageBreadcrumbs
        items={[
          { text: 'Models', icon: <ModelsIcon />, link: '/models' },
          { text: 'Upload model' },
        ]}
        mb={0.5}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Bring your own model — upload a pre-trained model, validate it with a
        live forward pass, and register it for deployment alongside
        platform-trained models.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {['Upload', 'Validate', 'Register'].map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activeStep === 0 && uploadStep}
      {activeStep === 1 && validateStep}
      {activeStep === 2 && registerStep}
      {activeStep === 3 && doneStep}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  upload: state.foundation_byom_upload,
  validation: state.foundation_byom_validate,
  registration: state.foundation_byom_register,
  segments: state.segments_list?.response?.segments || [],
});

const mapDispatchToProps = {
  uploadRequest,
  validateRequest,
  registerRequest,
  fetchSegments,
  clearUpload,
  clearValidate,
  clearRegister,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadModelPage);
